import { Link, Outlet, NavLink, useLocation } from 'react-router-dom'
import { LogOut, Menu, Wifi, X } from 'lucide-react'
import { Fragment } from 'react'
import type { ReactNode } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useConnectivity } from '../../hooks/useConnectivity'
import { OfflineBanner } from './OfflineBanner'
import { DiscoveryTabs } from './PublicHeader'
import { getDiscoveryTabs } from './discoveryTabs'
import { HeaderDiscoverySearch } from './DiscoverySearchContext'
import { getNavForContext, getNavForRole, mobileNavFor, navContextFor } from './RoleNav'
import {
  consoleThemeVars,
  getRoleLabel,
  getVerticalMeta,
  isConsoleRole,
  isDrivingVertical,
  useVertical,
  verticalThemeVars,
} from './useVertical'
import { VerticalBrand } from './VerticalBrand'
import { SwitchServiceButton } from './SwitchServiceButton'
import { NotificationBell } from './NotificationBell'
import { RoleSwitcherButton } from './RoleSwitcher'
import { ROLES, ROLE_HOME_PATHS } from './roleConfig'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMyProfile } from '../../api/queries/participants'
import { useCart } from '../../api/queries/cart'
import { setupAttention } from '../common/setupAttention'

export function AppShell({
  children,
  showWorkspaceBanner = true,
  headerCenter,
}: {
  children?: ReactNode
  showWorkspaceBanner?: boolean
  /**
   * Content for the header's center column (e.g. a vertical's search pill). When
   * provided it replaces the default discovery tabs / Farm-to-Table search — used by the
   * single-surface verticals (thrifts, homestay) to hoist their search pill into
   * the top header.
   */
  headerCenter?: ReactNode
} = {}) {
  const { activeRole, roles, logout, syncParticipant, switchRole } = useAuth()
  const isOnline = useConnectivity()
  const location = useLocation()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const profileQuery = useMyProfile()
  const profile = profileQuery.data
  const { activeVertical, setVertical } = useVertical()

  // Fulfiller UI lives only under Moves. Leaving Moves while activeRole is fulfiller
  // demotes to Buying; opening /fulfiller/* pins the vertical to Moves.
  useEffect(() => {
    const onFulfillerRoute =
      location.pathname.startsWith('/fulfiller') || location.pathname === '/become-fulfiller'
    if (onFulfillerRoute && !isDrivingVertical(activeVertical)) {
      setVertical('moves')
      return
    }
    if (activeRole === 'fulfiller' && !isDrivingVertical(activeVertical)) {
      switchRole('consumer')
      if (location.pathname.startsWith('/fulfiller')) {
        navigate('/consumer', { replace: true })
      }
    }
  }, [activeRole, activeVertical, location.pathname, navigate, setVertical, switchRole])

  // Marketplace roles resolve to a context (Buying / Work, Spec 45 §9); ops, admin and
  // onboarding keep their own navs, because a console spanning every vertical is not a
  // side of a transaction and does not belong in the same switch.
  const navContext = navContextFor(activeRole)
  const enrolments = profile?.supply_enrolments ?? {}
  // Union session + profile roles so "Become a Fulfiller" hides as soon as either source
  // knows the account fulfills (session can lag a render behind profile refetch).
  const heldRoles = Array.from(new Set([...(roles ?? []), ...(profile?.roles ?? [])]))
  const navItems = navContext
    ? getNavForContext(navContext, activeVertical, enrolments, heldRoles)
    : getNavForRole(activeRole, activeVertical, enrolments, heldRoles)
  // The vertical tile metaphor is for the demand/listing side only; cross-vertical
  // operators (ops/admin) filter, they don't switch. Work shows it too: selling is
  // vertical-scoped *inside* Work, and carrying is Moves-only inside Work.
  const showVerticalSwitcher = navContext !== null
  // Single-surface verticals (thrifts, homestay, moves) have one discovery tab and carry
  // their own in-content search pill via their discovery frame — so the header's
  // discovery tab and the vertical-switcher button are redundant there. Only the
  // multi-surface vertical (Farm-to-Table: Marketplace / Explore / Events) shows them.
  const isMultiSurfaceVertical = getDiscoveryTabs(activeVertical).length > 1
  const verticalMeta = getVerticalMeta(activeVertical)
  // Demand-side chrome (Buying / Work) follows the active vertical. Fulfiller Work is
  // Moves-branded. Ops/admin use soft-pink console theme — not FTT emerald (which is
  // also the :root default, so "no override" used to look like Farm-to-Table).
  const themeVars = showVerticalSwitcher
    ? verticalThemeVars(activeVertical)
    : isConsoleRole(activeRole)
      ? consoleThemeVars()
      : undefined
  const cartQuery = useCart(navContext === 'buying')
  const cartCount = cartQuery.data?.item_count ?? 0
  // Only require an exact match for items whose path is a prefix of another nav
  // item (e.g. role-home routes like /admin). Section items then stay highlighted
  // on their own detail routes (e.g. /admin/access/:participantId).
  const requiresExactMatch = (path: string) =>
    navItems.some((other) => other.path !== path && other.path.startsWith(`${path}/`))
  const showMobileTabBar =
    activeRole === 'consumer' || activeRole === 'creator' || activeRole === 'fulfiller'
  const mobileNavItems = mobileNavFor(navItems)
  const activeRoleConfig = ROLES.find((r) => r.key === activeRole)
  const ActiveRoleIcon = activeRoleConfig?.icon
  const isOverviewRoute = Boolean(activeRole && location.pathname === ROLE_HOME_PATHS[activeRole])
  // The Farm-to-Table-copy workspace banner is redundant on the non-Farm-to-Table consumer overview,
  // which renders its own vertical-specific header (Homestay/Thrifts Workspace).
  const showOverviewBanner =
    isOverviewRoute && !(activeRole === 'consumer' && activeVertical !== 'farm_to_table')
  const workspaceCopy = getWorkspaceCopy(activeRole)
  const onboardingIncomplete = (profile?.onboarding_status ?? 'complete') !== 'complete'

  /**
   * Sign out and land on the vertical's public home.
   *
   * Without this, clearing auth state on a protected route trips `RequireAuth`,
   * which redirects to `/login` — so signing out dumped the user on a full-page
   * login form. Dropping them on public discovery matches what they were doing;
   * the sign-in popup is one click away in the header if they want back in.
   */
  async function handleLogout() {
    await logout()
    navigate(verticalMeta.home, { replace: true })
  }

  useEffect(() => {
    if (!profile) return
    syncParticipant({
      participant_id: profile.participant_id,
      display_name: profile.display_name,
      phone_number: profile.phone_number,
      email: profile.email,
      roles: profile.roles,
      onboarding_status: profile.onboarding_status,
      id_verification_status: profile.id_verification_status,
      payout_status: profile.payout_status,
      payout_required_to_publish: profile.payout_required_to_publish,
      has_pin: profile.has_pin,
      subscription_status: profile.subscription_status,
      region_id: profile.region_id,
      status: profile.status,
    })
  }, [profile, syncParticipant])

  return (
    <div className="flex h-screen flex-col bg-stone-100 text-stone-950" style={themeVars}>
      <OfflineBanner isOffline={!isOnline} />

      {/* Top bar */}
      <header className="border-b border-stone-200 bg-white px-4 py-3 shadow-sm md:px-6">
        <div className="grid grid-cols-[auto_1fr] items-center gap-3 md:grid-cols-[auto_minmax(0,1fr)_auto]">
          {/* min-w-0 down the brand chain, or the whole page scrolls sideways on a
              phone. `truncate` sets white-space: nowrap, so the wordmark's
              min-content width is the full string — and a grid item's automatic
              minimum size is its min-content width. The auto track therefore could
              not shrink below "OSLE Farm-to-Table", the two tracks summed wider
              than the header, and every row (including the tabs/search row that
              spans them) inherited the overflow. min-w-0 restores the shrink the
              truncate was written to trigger. */}
          <div className="flex min-w-0 items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="rounded-xl border border-stone-200 p-2 text-stone-600 hover:bg-stone-50 md:hidden"
              aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            {showVerticalSwitcher ? (
              <Link
                to={verticalMeta.home}
                title={`${verticalMeta.label} home`}
                aria-label={`${verticalMeta.label} home`}
                className="flex min-w-0 items-center gap-3"
              >
                {/* Demand-side chrome names the vertical you're in. Operators
                    keep the neutral OSLE wordmark in the branch below.
                    Logo -> section home, per convention; switching service is
                    the grid button in the right-hand cluster. */}
                <VerticalBrand asHeading />
              </Link>
            ) : (
              <div className="flex min-w-0 items-center gap-3">
                <VerticalBrand asHeading neutral />
              </div>
            )}
          </div>

          {headerCenter ? (
            // Block (not flex) below md so the search bar fills its own row edge to
            // edge: as a flex item it shrank to the collapsed pill's content width
            // and sat centered, wasting most of the row. The desktop pills carry
            // their own `mx-auto max-w-*`, so md+ keeps flex centering.
            <div className="col-span-2 row-start-2 min-w-0 md:col-span-1 md:row-start-auto md:flex md:items-center md:justify-center">
              {headerCenter}
            </div>
          ) : isMultiSurfaceVertical ? (
            <div className="col-span-2 row-start-2 flex min-w-0 flex-col items-center gap-2.5 md:col-span-1 md:row-start-auto">
              <DiscoveryTabs className="max-w-full overflow-x-auto" />
              <HeaderDiscoverySearch />
            </div>
          ) : (
            // Keep the middle grid column on desktop so actions stay right-aligned;
            // render no extra row on mobile.
            <div className="hidden md:block" />
          )}

          {/* Global controls: who you are and cross-service actions. "Where you
              are" (and its home link) lives in the brand on the left. */}
          <div className="flex items-center justify-end gap-3 sm:gap-4">
            <RoleSwitcherButton />
            {showVerticalSwitcher && <SwitchServiceButton />}
            <NotificationBell />
            <button
              onClick={handleLogout}
              className="hidden items-center gap-2 rounded-2xl border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-500 shadow-sm hover:bg-red-50 hover:text-red-700 sm:flex"
              aria-label="Log out"
            >
              <LogOut className="h-4 w-4" />
              Log out
            </button>
          </div>
        </div>
      </header>

      <div className="relative flex flex-1 overflow-hidden">
        {/* Mobile backdrop */}
        {sidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 z-20 bg-stone-950/30 md:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close sidebar"
          />
        )}

        {/* Sidebar */}
        <nav
          className={`fixed inset-y-0 left-0 z-30 w-72 transform overflow-y-auto border-r border-stone-200 bg-white pt-20 transition-transform duration-200 ease-in-out md:static md:z-auto md:translate-x-0 md:pt-4 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="px-4">
            <div className="mb-4 rounded-2xl bg-stone-50 p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-stone-700">
                <Wifi className={`h-4 w-4 ${isOnline ? 'text-emerald-700' : 'text-red-700'}`} />
                {isOnline ? 'Connected' : 'No connection'}
              </div>
              <p className="mt-2 text-xs leading-5 text-stone-500">
                Write actions require connectivity. Cached read-only data remains visible.
              </p>
            </div>
            {/* A single "Navigation" heading only helps when the list is short.
                Ops carries nineteen entries, so it groups by task instead. */}
            {!navItems.some((item) => item.section) && (
              <div className="mb-3 px-2 text-xs font-semibold uppercase tracking-wide text-stone-400">
                Navigation
              </div>
            )}
            <div className="space-y-1.5">
              {navItems.map((item, index) => (
                <Fragment key={item.path}>
                  {item.section && item.section !== navItems[index - 1]?.section && (
                    <div
                      className={`px-2 text-xs font-semibold uppercase tracking-wide text-stone-400 ${
                        index === 0 ? 'mb-2' : 'mb-2 mt-5'
                      }`}
                    >
                      {item.section}
                    </div>
                  )}
                  <NavLink
                    to={item.path}
                    end={requiresExactMatch(item.path)}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-xl px-3 py-3 text-sm transition ${
                        isActive
                          ? 'bg-brand-950 font-medium text-white shadow-sm'
                          : 'text-stone-700 hover:bg-stone-100'
                      }`
                    }
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                    {item.path === '/cart' && cartCount > 0 && (
                      <span className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-[#c77b55] px-1.5 text-[10px] font-bold text-white">
                        {cartCount}
                      </span>
                    )}
                  </NavLink>
                </Fragment>
              ))}
            </div>
          </div>
          {/* No Security entry: `/settings/security` is only transaction-PIN management,
              and Profile → Settings already links there and shows whether a PIN is set.
              The amber banner below still routes anyone who has none. */}
          <button
            onClick={handleLogout}
            className="mt-4 flex w-full items-center gap-3 px-7 py-3 text-sm text-stone-500 hover:text-red-700 sm:hidden"
            aria-label="Log out"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </nav>

        {/* Content */}
        <main className="flex-1 overflow-y-auto bg-stone-100 p-4 pb-24 md:p-6">
          <div className="mx-auto max-w-[1500px] space-y-5">
            {/* An account past onboarding with no PIN is almost always ops/admin,
                provisioned out-of-band. The step-up guard fails closed, so every
                money action is blocked until they set one (DEF-29). */}
            {profile &&
              !profile.has_pin &&
              !onboardingIncomplete &&
              location.pathname !== '/settings/security' && (
                <section className={setupAttention.panelCompact}>
                  <p className="font-semibold">Transaction PIN not set</p>
                  <p className="mt-1 leading-6 text-stone-700">
                    Actions that move money are blocked until you set a transaction PIN.
                  </p>
                  <Link
                    to="/settings/security"
                    className="mt-3 inline-flex rounded-lg bg-stone-900 px-3 py-1.5 text-xs font-medium text-white"
                  >
                    Set your PIN
                  </Link>
                </section>
              )}
            {onboardingIncomplete && location.pathname !== '/onboarding' && (
              <section className={setupAttention.panel}>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 space-y-2">
                    <div className={setupAttention.chip}>
                      <span>Setup incomplete</span>
                    </div>
                    <h2 className="text-lg font-semibold text-stone-950">
                      Resume onboarding to unlock transactions and role tools
                    </h2>
                    <p className="max-w-2xl leading-6 text-stone-700">
                      You can keep browsing, but purchases, payouts, and workspace actions stay
                      limited until your account setup and verification are complete.
                    </p>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
                    <button
                      type="button"
                      onClick={() => navigate('/onboarding')}
                      className={setupAttention.cta}
                    >
                      Resume onboarding
                    </button>
                  </div>
                </div>
              </section>
            )}
            {showWorkspaceBanner && showOverviewBanner && (
              <section className="rounded-3xl border border-stone-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex rounded-full border border-brand-200 bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-800">
                        {ActiveRoleIcon && (
                          <ActiveRoleIcon className="mr-1.5 h-3.5 w-3.5 text-brand-700" />
                        )}
                        Active role:{' '}
                        {activeRoleConfig
                          ? getRoleLabel(
                              activeVertical,
                              activeRoleConfig.key,
                              activeRoleConfig.label,
                            )
                          : 'None'}
                      </span>
                    </div>
                    <h2 className="mt-3 text-2xl font-bold tracking-tight text-stone-950 md:text-3xl">
                      {workspaceCopy.title}
                    </h2>
                    <p className="mt-2 max-w-3xl text-sm text-stone-600 md:text-base">
                      {workspaceCopy.description}
                    </p>
                  </div>
                </div>
              </section>
            )}
            {children ?? <Outlet />}
          </div>
        </main>

        {showMobileTabBar && (
          <nav
            className="fixed inset-x-0 bottom-0 z-20 border-t border-stone-200 bg-white/95 px-2 py-2 shadow-[0_-8px_24px_rgba(28,25,23,0.08)] backdrop-blur md:hidden"
            aria-label="Primary mobile navigation"
          >
            <div className="grid grid-cols-5 gap-1">
              {mobileNavItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={requiresExactMatch(item.path)}
                  aria-label={item.label}
                  className={({ isActive }) =>
                    `relative flex min-h-14 items-center justify-center rounded-xl px-1 transition ${
                      isActive
                        ? 'bg-brand-950 text-white'
                        : 'text-stone-600 hover:bg-stone-100 hover:text-stone-950'
                    }`
                  }
                >
                  <item.icon className="h-6 w-6" />
                  {item.path === '/cart' && cartCount > 0 && (
                    <span className="absolute right-2 top-1.5 inline-flex min-w-4 items-center justify-center rounded-full bg-[#c77b55] px-1 text-[9px] font-bold text-white">
                      {cartCount}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </nav>
        )}
      </div>
    </div>
  )
}

function getWorkspaceCopy(role: string | null) {
  switch (role) {
    case 'consumer':
      return {
        title: 'Purchase Workspace',
        description: 'Track orders, payments, saved listings, and new supply opportunities.',
      }
    case 'creator':
      return {
        title: 'Creator Workspace',
        description: 'Manage supply, pickup handoffs, public presence, and demand signals.',
      }
    case 'fulfiller':
      return {
        title: 'Dispatch Workspace',
        description: 'Review offers, trips, vehicle details, and field incidents.',
      }
    case 'ops':
      return {
        title: 'Operations Workspace',
        description: 'Prioritize queues by risk, age, ownership, and next required action.',
      }
    case 'admin':
      return {
        title: 'Administration Workspace',
        description: 'Manage privileged configuration, staff access, and platform governance.',
      }
    default:
      return {
        title: 'Workspace',
        description: 'Review current work and continue from the highest-priority action.',
      }
  }
}
